import {
    Bot, UserPlus, ClipboardList, MessageSquare, Send, Timer,
    Package, BarChart3, ReceiptText, Users, ArrowRight, Calendar
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useBookDemo } from "@/context/BookDemoContext";

const FEATURES = [
    { icon: Bot, title: "AI kundemodtagelse", desc: "Indgående henvendelser bliver vurderet, struktureret og videresendt automatisk." },
    { icon: UserPlus, title: "Kundeoprettelse", desc: "Nye kunder oprettes hurtigt med alle relevante stamdata på plads." },
    { icon: ClipboardList, title: "Joboprettelse", desc: "Opret opgaver med adresse, beskrivelse, materialer og tidsestimat på få klik." },
    { icon: MessageSquare, title: "Kontorbeskeder", desc: "Kontoret kan kommunikere klart med medarbejdere uden støj fra SMS og opkald." },
    { icon: Send, title: "Telegram medarbejderflow", desc: "Medarbejderne får opgaver og opdaterer status direkte fra deres telefon." },
    { icon: Timer, title: "Automatisk tidsregistrering", desc: "Tid registreres løbende uden manuel indtastning eller bagudregning." },
    { icon: Package, title: "Materialer og noter", desc: "Materialeforbrug og noter samles direkte på opgaven — klar til faktura." },
    { icon: BarChart3, title: "Rapportering og statistik", desc: "Få indsigt i opgavetyper, tid, kunder og medarbejdernes flow." },
    { icon: ReceiptText, title: "Klar til faktura", desc: "Alt nødvendigt grundlag er samlet, så fakturering bliver hurtig og præcis." },
    { icon: Users, title: "Team-overblik", desc: "Se hvor jeres medarbejdere er, hvad de laver og hvad næste opgave bliver." },
];

export default function Funktioner() {
    const { open: openDemo } = useBookDemo();

    return (
        <div data-testid="page-funktioner">
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
                <div className="absolute -top-20 right-0 w-[420px] h-[420px] rounded-full bg-purple-200/30 blur-3xl pointer-events-none" />

                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-14 text-center">
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-[11px] font-semibold uppercase tracking-wider border border-blue-100">
                        Funktioner
                    </span>
                    <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[3.25rem] font-bold tracking-tight text-slate-900 leading-[1.05]">
                        Funktioner der{" "}
                        <span className="gradient-text">sparer tid i hverdagen.</span>
                    </h1>
                    <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
                        Alt samlet ét sted — fra kundehenvendelser til opfølgning, tid, materialer og rapportering.
                    </p>
                </div>
            </section>

            <section className="pb-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {FEATURES.map((f, i) => {
                            const Icon = f.icon;
                            return (
                                <div
                                    key={i}
                                    data-testid={`feature-card-${i}`}
                                    className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-md hover:-translate-y-0.5 hover:border-blue-200 transition-all"
                                >
                                    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-50 to-purple-50 text-blue-600 inline-flex items-center justify-center ring-1 ring-blue-100">
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <h3 className="mt-4 text-base font-semibold text-slate-900">{f.title}</h3>
                                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="pb-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="relative overflow-hidden rounded-3xl bg-white border border-slate-200 p-8 sm:p-12">
                        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-blue-100/60 blur-3xl" />
                        <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-purple-100/60 blur-3xl" />
                        <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                            <div>
                                <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                                    Vil du se hvordan funktionerne hænger sammen?
                                </h3>
                                <p className="mt-3 text-slate-600 max-w-lg text-sm sm:text-base">
                                    Vi viser dig flowet i en kort, fokuseret demo — tilpasset jeres måde at arbejde på.
                                </p>
                            </div>
                            <div className="md:justify-self-end flex gap-3 flex-wrap">
                                <Button data-testid="funktioner-cta-btn" onClick={openDemo} className="rounded-full h-12 px-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md">
                                    <Calendar className="w-4 h-4 mr-2" />
                                    Book gratis demo
                                </Button>
                                <Link to="/priser">
                                    <Button data-testid="funktioner-pricing-btn" variant="outline" className="rounded-full h-12 px-6 border-slate-300">
                                        Se priser <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
